import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Calendar, MapPin, ScanFace, ShoppingBag, ImageOff, Hash, Package } from "lucide-react";
import { Header } from "@/components/Header";
import { SelfieBar } from "@/components/SelfieCapture";
import { PhotoGrid } from "@/components/PhotoGrid";
import { api, previewUrl, brl, fmtDate, errMsg, CATEGORIES } from "@/lib/api";
import { useCart } from "@/lib/cart";

export default function EventPage() {
  const { id } = useParams();
  const cart = useCart();
  const [event, setEvent] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [searching, setSearching] = useState(false);
  const [matches, setMatches] = useState(null);
  const [all, setAll] = useState(null);
  const [tab, setTab] = useState("minhas");
  const [bib, setBib] = useState("");
  const [mode, setMode] = useState("face");

  const searchBib = async (e) => {
    e.preventDefault();
    if (!bib.trim()) return;
    setSearching(true);
    setTab("minhas");
    setMode("bib");
    try {
      const { data } = await api.get(`/events/${id}/bib/${encodeURIComponent(bib.trim())}`);
      setMatches(data);
      toast.success(data.length ? `Encontramos ${data.length} foto(s) com o número ${bib}!` : `Nenhuma foto com o número ${bib}.`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => setEvent(false));
  }, [id]);

  const search = async (file) => {
    setSelfie(file);
    setMode("face");
    setSearching(true);
    setTab("minhas");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/events/${id}/search`, fd);
      setMatches(data);
      toast.success(data.length ? `Encontramos ${data.length} foto(s) com você!` : "Nenhuma foto encontrada com seu rosto.");
      setTimeout(() => document.getElementById("resultados")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      toast.error(errMsg(e));
      setMatches(null);
    } finally {
      setSearching(false);
    }
  };

  const showAll = async () => {
    setTab("todas");
    if (!all) setAll((await api.get(`/events/${id}/photos`)).data);
  };

  if (event === false) return <div className="p-10 text-center" data-testid="event-not-found">Evento não encontrado. <Link to="/" className="underline">Voltar</Link></div>;
  if (!event) return <div className="min-h-screen grid place-items-center text-zinc-500">Carregando…</div>;

  const list = tab === "minhas" ? matches : all;
  const toItem = (p) => ({ id: p.id, event_id: id, event_name: event.name, price: event.price, package_price: event.package_price });
  const eventCartCount = cart.items.filter((i) => i.event_id === id).length;

  return (
    <div className="min-h-screen bg-white pb-28">
      <Header />
      <section className="relative h-[42vh] min-h-[300px] bg-zinc-900 overflow-hidden">
        {event.cover_photo_id && <img src={previewUrl(event.cover_photo_id)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-black/10" />
        <div className="relative max-w-7xl mx-auto h-full px-5 sm:px-8 flex flex-col justify-end pb-8 text-white">
          <Link to="/" data-testid="back-to-events-link" className="absolute top-6 left-5 sm:left-8 flex items-center gap-1.5 text-sm text-white/85 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Eventos
          </Link>
          <span className="label-caps text-white/80">{CATEGORIES[event.category] || event.category}</span>
          <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none" data-testid="event-title">{event.name}</h1>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/85">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{fmtDate(event.date)}</span>
            {event.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{event.location}</span>}
            <span data-testid="event-price">{brl(event.price)} por foto · {event.photo_count} fotos</span>
          </div>
        </div>
      </section>

      <section id="resultados" className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-center justify-between gap-4 border-b border-zinc-200">
          <div className="flex">
            {[["minhas", "Minhas fotos"], ["todas", "Todas as fotos"]].map(([k, l]) => (
              <button
                key={k}
                onClick={() => (k === "todas" ? showAll() : setTab(k))}
                data-testid={`tab-${k}`}
                className={`h-12 px-4 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-[#0055FF] text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
              >
                {l}
                {k === "minhas" && matches ? ` (${matches.length})` : ""}
              </button>
            ))}
          </div>
          {eventCartCount > 0 && (
            <Link to="/carrinho" data-testid="go-to-cart-btn" className="hidden sm:flex items-center gap-2 h-10 px-4 rounded-md bg-zinc-900 text-white text-sm font-semibold">
              <ShoppingBag className="w-4 h-4" /> Ver carrinho ({cart.items.length})
            </Link>
          )}
        </div>

        <form onSubmit={searchBib} className="mt-6 flex gap-2 max-w-md" data-testid="bib-search-form">
          <div className="relative flex-1">
            <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              value={bib}
              onChange={(e) => setBib(e.target.value.replace(/\D/g, ""))}
              inputMode="numeric"
              placeholder="Nº de peito"
              data-testid="bib-search-input"
              className="w-full h-12 pl-9 pr-3 rounded-md border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#0055FF]"
            />
          </div>
          <button disabled={searching || !bib} data-testid="bib-search-btn" className="h-12 px-5 rounded-md bg-zinc-900 hover:bg-[#0055FF] disabled:opacity-50 text-white font-semibold transition-colors">
            Buscar
          </button>
        </form>

        <div className="mt-6">
          {tab === "minhas" && matches === null && !searching && (
            <div className="py-16 max-w-md" data-testid="search-intro">
              <ScanFace className="w-10 h-10 text-[#0055FF]" />
              <h2 className="mt-4 font-display font-bold text-2xl tracking-tight">Encontre suas fotos com uma selfie</h2>
              <p className="mt-2 text-zinc-600">Use o botão abaixo para tirar uma selfie ou enviar uma foto do seu rosto — ou busque pelo seu número de peito. Dica: rosto de frente, sem óculos escuros e com boa luz.</p>
            </div>
          )}
          {searching && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[4/5] rounded-md bg-zinc-100 animate-pulse" />)}
            </div>
          )}
          {!searching && list && list.length === 0 && (
            <div className="py-16 text-zinc-600 flex items-center gap-3" data-testid="no-results">
              <ImageOff className="w-6 h-6" /> {tab === "minhas" ? (mode === "bib" ? "Nenhuma foto com esse número de peito. Confira o número ou tente uma selfie." : "Não encontramos fotos com seu rosto. Tente outra selfie ou veja todas as fotos.") : "Este evento ainda não tem fotos."}
            </div>
          )}
          {!searching && list && list.length > 0 && (
            <>
              {tab === "minhas" && <PackageCta list={list} event={event} onAdd={() => cart.addMany(list.map(toItem))} />}
              <PhotoGrid photos={list} event={event} />
            </>
          )}
        </div>
      </section>

      <SelfieBar onSelfie={search} selfie={selfie} searching={searching} onReset={() => { setSelfie(null); setMatches(null); }} />
    </div>
  );
}

function PackageCta({ list, event, onAdd }) {
  const sum = list.length * event.price;
  const pkg = Number(event.package_price || 0);
  const isPkg = pkg > 0 && sum > pkg;
  return (
    <div className={`mb-5 rounded-lg border p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${isPkg ? "border-[#0055FF] bg-[#0055FF]/5" : "border-zinc-200"}`} data-testid="package-cta">
      <div className="flex items-center gap-3">
        <Package className="w-6 h-6 text-[#0055FF] shrink-0" />
        <div>
          <div className="font-semibold">{isPkg ? `Pacote: todas as minhas ${list.length} fotos` : list.length === 1 ? "Levar esta foto" : `Levar todas as ${list.length} fotos`}</div>
          <div className="text-sm text-zinc-600">
            {isPkg ? (<><span className="line-through mr-2">{brl(sum)}</span><span className="font-semibold text-[#0055FF]" data-testid="package-price">{brl(pkg)}</span> · economize {brl(sum - pkg)}</>) : brl(sum)}
          </div>
        </div>
      </div>
      <button onClick={onAdd} data-testid="add-all-matches-btn" className="h-11 px-5 rounded-md bg-[#0055FF] hover:bg-[#0040CC] text-white text-sm font-semibold transition-colors">
        Adicionar todas ao carrinho
      </button>
    </div>
  );
}
